// ============================================================
// transcript — エージェントの会話を「そのまま読める形」に整形する
//
// 正: booking-platform/md/ai-control-center.md §15-7
//
// 「この件は記録して」で保存する本文を作る。後から人が読んで
// 「何を頼んで、AI が何をして、どこで失敗したか」を追えることが目的なので、
// 要約せず、ツール実行の引数と結果もそのまま残す。
// ============================================================
import type { AgentUiMessage } from './types';

/** 長すぎる値は切る（1件あたりの上限。全体は保存時にサーバ側でも制限する） */
function clip(v: any, max = 1200): string {
  let s: string;
  try {
    s = typeof v === 'string' ? v : JSON.stringify(v ?? '');
  } catch {
    s = String(v ?? '');
  }
  return s.length > max ? `${s.slice(0, max)}…（以下略）` : s;
}

const TIME = (iso: string) => {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleString('ja-JP');
};

export function buildTranscript(messages: AgentUiMessage[]): string {
  const lines: string[] = [];
  for (const m of messages) {
    const t = TIME(m.createdAt);
    switch (m.kind) {
      case 'user':
        lines.push(`【オペレータ】${t}\n${m.text}`);
        break;
      case 'assistant':
        lines.push(`【エージェント】${t}\n${m.text}`);
        break;
      case 'activity': {
        const head = `【ツール実行】${t} ${m.toolName} → ${m.status}`
          + (m.durationMs != null ? `（${m.durationMs}ms）` : '');
        const body = [`  引数: ${clip(m.args, 600)}`];
        if (m.status === 'error') body.push(`  エラー: ${clip(m.errorMessage, 600)}`);
        else if (m.result !== undefined) body.push(`  結果: ${clip(m.result)}`);
        lines.push(`${head}\n${body.join('\n')}`);
        break;
      }
      case 'confirm':
        lines.push(`【確認カード（予約作成）】${t} → ${labelOfState(m.state)}\n  内容: ${clip(m.args, 600)}`);
        break;
      case 'update_confirm': {
        const diff = (m.changes || [])
          .map((c) => `    ${c.label}: ${c.before} → ${c.after}`)
          .join('\n');
        lines.push(`【確認カード（変更）】${t} → ${labelOfState(m.state)}\n${diff}`);
        break;
      }
      case 'plan': {
        const steps = (m.steps || []).map((st, i) => `    ${i + 1}. [${st.status}] ${st.label}（${st.actor}・${st.check}）${st.note ? ` — ${st.note}` : ''}`).join('\n');
        lines.push(`【手順カード】${t} ${m.title} → ${m.state}\n${steps}`);
        break;
      }
      default:
        // 未知の種別も黙って捨てず、種別だけは残す
        lines.push(`【${(m as any).kind}】${t}`);
    }
  }
  return lines.join('\n\n');
}

function labelOfState(state: string): string {
  if (state === 'approved') return '承認';
  if (state === 'rejected') return '取消';
  return '未応答';
}
